import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, CheckCircle2Icon, EyeIcon, EyeOffIcon, LockKeyholeIcon, MailIcon, ShieldCheckIcon, UserIcon } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { RDReadyLogo } from '../../RDReadyLogo';
type AuthMode = 'login' | 'signup';
function mapAuthError(err: unknown): string {
  const code = typeof err === 'object' && err && 'code' in err ? String(err.code) : '';
  if (code === 'auth/email-already-in-use') {
    return 'An account with this email already exists. Try signing in instead.';
  }
  if (code === 'auth/invalid-credential' || code === 'auth/wrong-password' || code === 'auth/user-not-found') {
    return 'Invalid email or password. Please try again.';
  }
  if (code === 'auth/weak-password') {
    return 'Password must be at least 6 characters.';
  }
  if (code === 'auth/invalid-email') {
    return 'Please enter a valid email address.';
  }
  if (code === 'auth/too-many-requests') {
    return 'Too many attempts. Please wait a moment and try again.';
  }
  return 'Something went wrong. Please try again.';
}
const BLUE = '#2563EB';
const TEAL = '#0F9E8E';
const FONT = '"Poppins", "Inter", system-ui, sans-serif';
function RDReadyMark() {
  return <Link to="/" className="inline-flex rounded-lg bg-white px-3 py-2 shadow-sm" aria-label="RD Ready home" data-id="element-1727">
      <RDReadyLogo className="h-8 w-36" data-id="element-1728" />
    </Link>;
}
function PasswordField({
  value,
  onChange,
  minLength,
  placeholder
}: {
  value: string;
  onChange: (value: string) => void;
  minLength?: number;
  placeholder: string;
}) {
  const [visible, setVisible] = useState(false);
  return <div className="relative" data-id="element-1729">
      <LockKeyholeIcon className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#94A3B8]" aria-hidden="true" data-id="element-1730" />
      <input id="password" name="password" type={visible ? 'text' : 'password'} required minLength={minLength} value={value} onChange={event => onChange(event.target.value)} className="w-full rounded-xl border border-[#E2E8F0] bg-white py-3.5 pl-11 pr-12 text-sm text-[#0F172A] outline-none transition-colors placeholder:text-[#94A3B8] focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/15" placeholder={placeholder} autoComplete={minLength ? 'new-password' : 'current-password'} data-id="element-1731" />
      <button type="button" onClick={() => setVisible(current => !current)} className="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center text-[#94A3B8] transition-colors hover:text-[#2563EB] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]" aria-label={visible ? 'Hide password' : 'Show password'} data-id="element-1732">
        {visible ? <EyeOffIcon className="h-4 w-4" data-id="element-1733" /> : <EyeIcon className="h-4 w-4" data-id="element-1734" />}
      </button>
    </div>;
}
function AuthMessage({
  type,
  children
}: {
  type: 'error' | 'success';
  children: React.ReactNode;
}) {
  const isSuccess = type === 'success';
  return <div className={`flex gap-3 rounded-xl border p-4 text-sm leading-relaxed ${isSuccess ? 'border-[#C7EFE8] bg-[#F0FBF9] text-[#0F766E]' : 'border-[#FBD5D0] bg-[#FEF2F2] text-[#B91C1C]'}`} role={isSuccess ? 'status' : 'alert'} data-id="element-1735">
      {isSuccess ? <CheckCircle2Icon className="mt-0.5 h-4 w-4 shrink-0" data-id="element-1736" /> : <ShieldCheckIcon className="mt-0.5 h-4 w-4 shrink-0" data-id="element-1737" />}
      <p data-id="element-1738">{children}</p>
    </div>;
}
function AuthFrame({
  mode,
  children
}: {
  mode: AuthMode | 'reset';
  children: React.ReactNode;
}) {
  const title = mode === 'login' ? 'Welcome back.' : mode === 'signup' ? 'Set up your study space.' : 'Reset your password.';
  const description = mode === 'login' ? 'Sign in to continue your focused CNSC preparation.' : mode === 'signup' ? 'Create your account to keep your study access and progress in one place.' : 'We’ll send a secure reset link to your email address.';
  return <div className="w-full bg-white" style={{
    fontFamily: FONT
  }} data-id="element-1739">
      <div className="mx-auto grid min-h-[calc(100vh-72px)] max-w-[1440px] lg:grid-cols-[.95fr_1.05fr]" data-id="element-1740">
        <aside className="relative hidden overflow-hidden px-10 py-12 text-white lg:flex lg:flex-col xl:px-16" style={{
        backgroundColor: BLUE
      }} data-id="element-1741">
          <RDReadyMark data-id="element-1742" />

          <motion.div initial={{
          opacity: 0,
          y: 18
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.55,
          ease: [0.2, 0.65, 0.3, 0.9]
        }} className="my-auto max-w-md" data-id="element-1743">
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/70" data-id="element-1744">
              Member study space
            </p>
            <h1 className="mt-5 text-5xl font-extrabold leading-[1.04] tracking-tight text-white xl:text-6xl" data-id="element-1745">
              Make every study session count.
            </h1>
            <p className="mt-6 text-base leading-relaxed text-white/85" data-id="element-1746">
              Return to your quizzes, active flashcards, and personalized topic
              review whenever you’re ready.
            </p>

            <div className="mt-12 border-y border-white/20 py-7" data-id="element-1747">
              {['Practice questions with rationales', 'Active-recall flashcard review', 'Clear progress across key topics'].map((item, index) => <div key={item} className="flex items-center gap-4 py-2.5 text-sm text-white/90" data-id="element-1748">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white/15 text-[10px] font-bold text-white" data-id="element-1749">
                    0{index + 1}
                  </span>
                  {item}
                </div>)}
            </div>
          </motion.div>

          <p className="max-w-sm text-xs leading-relaxed text-white/70" data-id="element-1750">
            RD Ready is an independent study resource and is not affiliated with
            or endorsed by the NBNSC.
          </p>
        </aside>

        <main className="flex min-h-[calc(100vh-72px)] items-center px-5 py-12 sm:px-8 lg:px-14 xl:px-20" data-id="element-1751">
          <div className="mx-auto w-full max-w-[430px]" data-id="element-1752">
            <motion.div initial={{
            opacity: 0,
            y: 14
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.45,
            ease: [0.2, 0.65, 0.3, 0.9]
          }} data-id="element-1753">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{
              color: BLUE
            }} data-id="element-1754">
                {mode === 'login' ? 'Member sign in' : mode === 'signup' ? 'Create account' : 'Account recovery'}
              </p>
              <h2 className="mt-4 text-4xl font-extrabold leading-tight tracking-tight text-[#0F172A] sm:text-5xl" data-id="element-1755">
                {'Welcome back!'}
              </h2>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-[#5A6B7B]" data-id="element-1756">
                {'Sign in to continue your dietitian journey!'}
              </p>
            </motion.div>

            <div className="mt-9" data-id="element-1757">{children}</div>

            <p className="mt-9 border-t border-[#EDF1F7] pt-5 text-center text-xs leading-relaxed text-[#94A3B8]" data-id="element-1758">
              Need study access?{' '}
              <Link to="/cnsc-exam" className="font-bold underline underline-offset-4" style={{
              color: BLUE
            }} data-id="element-1759">
                Explore RD Ready study access
              </Link>
            </p>
          </div>
        </main>
      </div>
    </div>;
}
function AuthForm({
  mode,
  redirectTo
}: {
  mode: AuthMode;
  redirectTo?: string;
}) {
  const navigate = useNavigate();
  const {
    signIn,
    signUp
  } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isLogin = mode === 'login';
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    if (!isLogin && !name.trim()) {
      setError('Please enter your name.');
      return;
    }
    setLoading(true);
    try {
      if (isLogin) {
        await signIn(email, password);
        navigate(redirectTo ?? '/account', {
          replace: true
        });
      } else {
        await signUp(email, password, name.trim(), marketingOptIn);
        navigate(redirectTo ?? '/account', {
          replace: true
        });
      }
    } catch (err) {
      setError(mapAuthError(err));
    } finally {
      setLoading(false);
    }
  };
  return <form onSubmit={handleSubmit} className="space-y-5" data-id="element-1760">
      {!isLogin && <div data-id="element-1761">
          <label htmlFor="name" className="mb-2 block text-xs font-bold text-[#334155]" data-id="element-1762">
            Full name
          </label>
          <div className="relative" data-id="element-1763">
            <UserIcon className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#94A3B8]" aria-hidden="true" data-id="element-1764" />
            <input id="name" name="name" type="text" required value={name} onChange={event => setName(event.target.value)} className="w-full rounded-xl border border-[#E2E8F0] bg-white py-3.5 pl-11 pr-4 text-sm text-[#0F172A] outline-none transition-colors placeholder:text-[#94A3B8] focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/15" placeholder="Your name" autoComplete="name" data-id="element-1765" />
          </div>
        </div>}

      <div data-id="element-1766">
        <label htmlFor="email" className="mb-2 block text-xs font-bold text-[#334155]" data-id="element-1767">
          Email address
        </label>
        <div className="relative" data-id="element-1768">
          <MailIcon className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#94A3B8]" aria-hidden="true" data-id="element-1769" />
          <input id="email" name="email" type="email" required value={email} onChange={event => setEmail(event.target.value)} className="w-full rounded-xl border border-[#E2E8F0] bg-white py-3.5 pl-11 pr-4 text-sm text-[#0F172A] outline-none transition-colors placeholder:text-[#94A3B8] focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/15" placeholder="you@example.com" autoComplete="email" data-id="element-1770" />
        </div>
      </div>

      <div data-id="element-1771">
        <div className="mb-2 flex items-center justify-between gap-4" data-id="element-1772">
          <label htmlFor="password" className="text-xs font-bold text-[#334155]" data-id="element-1773">
            Password
          </label>
          {isLogin && <Link to="/forgot-password" className="text-xs font-bold underline underline-offset-4" style={{
          color: BLUE
        }} data-id="element-1774">
              Forgot password?
            </Link>}
        </div>
        <PasswordField value={password} onChange={setPassword} minLength={isLogin ? undefined : 6} placeholder={isLogin ? 'Enter your password' : 'At least 6 characters'} data-id="element-1775" />
      </div>

      {!isLogin && <label className="flex cursor-pointer items-start gap-3 pt-1 text-sm leading-relaxed text-[#5A6B7B]" data-id="element-1776">
          <input type="checkbox" checked={marketingOptIn} onChange={event => setMarketingOptIn(event.target.checked)} className="mt-0.5 h-4 w-4 accent-[#2563EB]" data-id="element-1777" />
          <span data-id="element-1778">
            Send me occasional RD Ready study updates. You can unsubscribe at
            any time.
          </span>
        </label>}

      {error && <AuthMessage type="error" data-id="element-1779">{error}</AuthMessage>}

      <button type="submit" disabled={loading} className="inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-4 text-xs font-bold uppercase tracking-[0.14em] text-white shadow-sm transition-transform hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0" style={{
      backgroundColor: BLUE
    }} data-id="element-1780">
        {loading ? <>
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" data-id="element-1781" />
            {isLogin ? 'Signing in…' : 'Creating account…'}
          </> : <>
            {isLogin ? 'Sign in to study' : 'Create my account'}
            <ArrowRightIcon className="h-4 w-4" data-id="element-1782" />
          </>}
      </button>

      <p className="pt-1 text-center text-sm text-[#5A6B7B]" data-id="element-1783">
        {isLogin ? 'New to RD Ready?' : 'Already have an account?'}{' '}
        <Link to={isLogin ? '/signup' : '/login'} state={redirectTo ? {
        from: redirectTo
      } : undefined} className="font-bold underline underline-offset-4" style={{
        color: BLUE
      }} data-id="element-1784">
          {isLogin ? 'Create an account' : 'Sign in'}
        </Link>
      </p>
    </form>;
}
export function Login() {
  const location = useLocation();
  const redirectTo = (location.state as {
    from?: string;
  } | null)?.from ?? '/account';
  return <AuthFrame mode="login" data-id="element-1785">
      <AuthForm mode="login" redirectTo={redirectTo} data-id="element-1786" />
    </AuthFrame>;
}
export function Signup() {
  const location = useLocation();
  const redirectTo = (location.state as {
    from?: string;
  } | null)?.from ?? '/account';
  return <AuthFrame mode="signup" data-id="element-1787">
      <AuthForm mode="signup" redirectTo={redirectTo} data-id="element-1788" />
    </AuthFrame>;
}
export function ForgotPassword() {
  const {
    resetPassword
  } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await resetPassword(email);
      setSent(true);
    } catch (err) {
      setError(mapAuthError(err));
    } finally {
      setLoading(false);
    }
  };
  return <AuthFrame mode="reset" data-id="element-1789">
      {sent ? <div className="space-y-6" data-id="element-1790">
          <AuthMessage type="success" data-id="element-1791">
            If an account exists for <strong data-id="element-1792">{email}</strong>, we’ve sent a
            password reset link. Check your spam folder if you don’t see it.
          </AuthMessage>
          <Link to="/login" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] hover:opacity-80" style={{
        color: BLUE
      }} data-id="element-1793">
            <ArrowLeftIcon className="h-4 w-4" data-id="element-1794" />
            Back to sign in
          </Link>
        </div> : <form onSubmit={handleSubmit} className="space-y-5" data-id="element-1795">
          <div data-id="element-1796">
            <label htmlFor="email" className="mb-2 block text-xs font-bold text-[#334155]" data-id="element-1797">
              Email address
            </label>
            <div className="relative" data-id="element-1798">
              <MailIcon className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#94A3B8]" aria-hidden="true" data-id="element-1799" />
              <input id="email" name="email" type="email" required value={email} onChange={event => setEmail(event.target.value)} className="w-full rounded-xl border border-[#E2E8F0] bg-white py-3.5 pl-11 pr-4 text-sm text-[#0F172A] outline-none transition-colors placeholder:text-[#94A3B8] focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/15" placeholder="you@example.com" autoComplete="email" data-id="element-1800" />
            </div>
          </div>

          {error && <AuthMessage type="error" data-id="element-1801">{error}</AuthMessage>}

          <button type="submit" disabled={loading} className="inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-4 text-xs font-bold uppercase tracking-[0.14em] text-white shadow-sm transition-transform hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0" style={{
        backgroundColor: BLUE
      }} data-id="element-1802">
            {loading ? <>
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" data-id="element-1803" />
                Sending link…
              </> : <>
                Send reset link
                <ArrowRightIcon className="h-4 w-4" data-id="element-1804" />
              </>}
          </button>

          <p className="pt-1 text-center text-sm text-[#5A6B7B]" data-id="element-1805">
            Remembered it?{' '}
            <Link to="/login" className="font-bold underline underline-offset-4" style={{
          color: BLUE
        }} data-id="element-1806">
              Back to sign in
            </Link>
          </p>
        </form>}
    </AuthFrame>;
}